import { useState } from 'react';
import { Button, Drawer, List, Skeleton, Space } from 'antd';
import { LogOut, Moon, Settings, Sun } from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';

import darkVars from '@/dark.json';
import lightVars from '@/light.json';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { authOperations } from '@/state/ducks/auth';
import { useEmployeeInfo } from '@/core/domain/enterprise/Employees/Employees.hooks';
import { getUserFromLocalStorage } from '@/utils/user';
import { UserAvatar } from '@/components/_common';
import AboutThePlatformModal from '../AboutThePlatformModal';
import { useDisclosure } from '@/hooks';
import { useColorScheme } from '@/contexts/ColorSchemeContextProvider';

interface AppBarUserDrawerMenuProps {
  openUser: boolean;
  closeUser: () => void;
}

declare global {
  interface Window {
    less: {
      modifyVars: (vars: unknown) => void;
    };
  }
}

export default function AppBarUserDrawerMenu({
  openUser,
  closeUser,
}: AppBarUserDrawerMenuProps) {
  const { employeeId, userEmail } = getUserFromLocalStorage();
  const intl = useIntl().formatMessage;
  const history = useHistory();
  const dispatch = useDispatch();
  const [isDarkModeOn, setIsDarkModeOn] = useState(
    localStorage.getItem('app-theme') === 'dark',
  );
  const [, setColorScheme] = useColorScheme();

  const [modalAbout, modalAboutAction] = useDisclosure();

  const { data, isLoading } = useEmployeeInfo(
    openUser ? Number(employeeId) : undefined,
  );

  const handleTheme = () => {
    const vars = !isDarkModeOn ? darkVars : lightVars;

    localStorage.setItem('app-theme', !isDarkModeOn ? 'dark' : 'light');
    setIsDarkModeOn(!isDarkModeOn);
    setColorScheme(!isDarkModeOn ? 'dark' : 'light');

    try {
      window.less.modifyVars(vars);
    } catch {}
  };

  const handlePushTo = (link: string) => {
    closeUser();
    history.push(link);
  };

  const handleLogoutClick = () => {
    dispatch(authOperations.handleLogout());

    history.push('/');
  };

  return (
    <>
      <Drawer
        open={openUser}
        onClose={closeUser}
        title="Usuário"
        footer={
          <Button
            danger
            type="link"
            style={{ width: '100%' }}
            onClick={handleLogoutClick}
          >
            <Space>
              <LogOut />
              {intl({ id: 'layout.appBar.userDropdown.logout' })}
            </Space>
          </Button>
        }
      >
        <List>
          <List.Item>
            <Skeleton loading={isLoading}>
              <List.Item.Meta
                avatar={
                  <UserAvatar
                    gender={data?.gender}
                    picture={data?.photo}
                    size={40}
                  />
                }
                title={data?.name}
                description={userEmail}
              />
            </Skeleton>
          </List.Item>
          <List.Item onClick={handleTheme}>
            {isDarkModeOn ? (
              <Space>
                <Sun />
                {intl({ id: 'layout.appBar.userDropdown.lightMode' })}
              </Space>
            ) : (
              <Space>
                <Moon />
                {intl({ id: 'layout.appBar.userDropdown.nightMode' })}
              </Space>
            )}
          </List.Item>
          <List.Item onClick={() => handlePushTo('/app/configProfile/account')}>
            <Space>
              <Settings />
              Configurações de Perfil
            </Space>
          </List.Item>
          <Space
            direction="vertical"
            size="large"
            style={{ width: '100%', marginTop: '2em' }}
          >
            <Button
              type="link"
              onClick={modalAboutAction.open}
              style={{ padding: 0, margin: 0 }}
            >
              <FormattedMessage id="layout.appBar.userDropdown.about" />
            </Button>

            <Button
              type="link"
              onClick={() => window.open('/privacyPolicy', '_blank')}
              style={{ padding: 0, margin: 0 }}
            >
              <FormattedMessage id="layout.appBar.userDropdown.privacy" />
            </Button>
          </Space>
        </List>
      </Drawer>

      <AboutThePlatformModal
        open={modalAbout}
        onCancel={modalAboutAction.close}
      />
    </>
  );
}
